<template>
    <div class="transaction-details">
        <loading-spinner v-if="loading" />

        <template v-else>
            <div v-if="isCancellable && statusCancel"
                id="order-details-btn-cancel"
                class="button button--grey transaction-details__cancel-order"
                @click="showCancelConfirmation"
            >
                <loading-spinner v-if="isCancelling"
                    :customStyles="loadingSpinnerStyles"
                />
                <span v-else>{{ $t('marketplace.orderDetails.cancelOrder') }}</span>
            </div>
            <div v-for="(group, index) in transaction.sections"
                :key="index"
                class="transaction-details__group"
            >
                <div class="transaction-details__title">
                    {{ group.title }}
                </div>

                <div v-for="(row, groupIndex) in group.rows"
                    :key="groupIndex"
                    class="transaction-details__row"
                >
                    <div v-for="(field, subIndex) in row.fields"
                        :key="subIndex"
                        class="transaction-details__column"
                    >
                        <div class="transaction-details__subtitle">{{ field.label }}</div>

                        <div v-if="field.isCopyable"
                            class="transaction-details__text transaction-details__text--green"
                            @click="handleClick(field)"
                        >
                            {{ field.formattedValue }}
                        </div>

                        <div v-else
                            class="transaction-details__text"
                        >
                            {{ field.formattedValue }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
// Helper
import {
    copyToClipboard,
} from '@webapp/helpers/generic.js';

// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Socios variables
import socios from '@webapp/socios.json';

// Shared types
import {
    TransactionDetailsFieldDataType,
} from '@shared/types/store/transactions';

// Types
import localStoreTypes from '@webapp/store/types.js';
import localEpicTypes from '@webapp/epics/types.js';

// Utils
import {
    findErrorBy,
} from '@shared/utils/error.js';

// Components
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

// Mixins
import pollingMixin from '@shared/mixins/generic-polling-mixin.ts';

export default {
    name: 'wallet-transaction-details',
    components: {
        loadingSpinner,
    },
    mixins: [pollingMixin],
    props: {
        transactionId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            isCancelling: false,
            statusCancel: true,
        };
    },
    computed: {
        ...mapGetters([
            'isPartnerApp',
            'getLatestSimpleValidationError',
        ]),

        ...mapGetters('transactions', ['currentTransactionDetails']),

        ...mapGetters('marketplace', ['cancelledTradingOrder']),

        transaction() {
            return this.currentTransactionDetails;
        },

        isStatusCancelling() {
            return this.transaction?.summary?.status?.toLowerCase() === 'cancelling';
        },

        isCancellable() {
            const { extraMetadata } = (this.transaction ?? {});

            return !this.isStatusCancelling && (extraMetadata?.isCancellableMarketPlace || extraMetadata?.isCancellableFTO);
        },

        loadingSpinnerStyles() {
            return {
                height: 0,
                padding: `${1.5}rem`,
            };
        },
    },
    watch: {
        currentTransactionDetails: {
            deep: true,
            immediate: true,
            handler(details) {
                if (details.id) {
                    this.loading = false;
                }

                if (!this.isCancellable) {
                    this.pollingMixinTerminatePolling();
                }
            },
        },

        cancelledTradingOrder() {
            this.$notify({
                type: 'success',
                text: this.$t('marketplace.orderDetails.successCancel'),
            });

            this.isCancelling = false;
            this.statusCancel = false;

            if (this.isCancellable) {
                this.pollingMixinStartPolling(
                    false,
                    (10 * 1000),
                    this.fetchTransactionDetails({
                        transactionId: this.transactionId,
                        chilizCurrencyId: socios.chilizCurrencyId,
                    })
                );
            }
        },

        getLatestSimpleValidationError({ errors }) {
            const orderCannotBeCancelledErrorCode = findErrorBy('type', 'code', 'orderCannotBeCancelled');
            const orderCannotBeCancelledError = errors.find(({ code }) => code === orderCannotBeCancelledErrorCode);

            if (orderCannotBeCancelledError) {
                this.$notify({
                    type: 'error',
                    text: orderCannotBeCancelledError.title,
                });

                this.isCancelling = false;
                this.statusCancel = false;
            }
        },
    },
    async mounted() {
        this.loading = true;

        await this.fetchTransactionDetails({
            transactionId: this.transactionId,
            chilizCurrencyId: socios.chilizCurrencyId,
        });

        if (this.currentTransactionDetails.id) {
            this.loading = false;
        }

        if (this.isPartnerApp) {
            this.setPartnerTransactionDetails();
        }
    },
    methods: {
        ...mapActions('transactions', ['fetchTransactionDetails']),

        ...mapActions({
            cancelOrder: localEpicTypes.CANCEL_TRADE_ORDER,
            setModal: localStoreTypes.SET_MODAL,
            resetModal: localStoreTypes.RESET_MODAL,
        }),

        handleClick(field) {
            if (field.dataType === TransactionDetailsFieldDataType.Link && field.data.value) {
                window.open(field.data.value, '_blank');
            } else if (field.isCopyable && field.data.value) {
                copyToClipboard(field.data.value, this);
            }
        },

        // Removes blockchain transaction details for Partner AppView.
        setPartnerTransactionDetails() {
            if (this.currentTransactionDetails.sections.length >= 3) {
                this.currentTransactionDetails.sections.splice(2, 1);
            }
        },

        showCancelConfirmation() {
            this.setModal({
                isVisible: true,
                isClosable: true,
                image: require('@webapp/assets/oopps.webp'),
                title: this.$t('marketplace.orderDetails.cancelConfirmTitle'),
                messages: [
                    {
                        text: `${this.$t('marketplace.orderDetails.cancelConfirmMessage')}\n${this.$t('marketplace.orderDetails.cancelDisclaimer')}`,
                    },
                ],
                acceptButton: {
                    text: this.$t('marketplace.orderDetails.cancelConfirmOk'),
                    action: () => {
                        this.cancelOrder({
                            orderId: this.transaction.extraMetadata?.buyOrderId,
                        });
                        this.isCancelling = true;
                        this.resetModal();
                    },
                },
                cancelButton: {
                    text: this.$t('marketplace.orderDetails.cancelConfirmCancel'),
                    action: () => this.resetModal(),
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.transaction-details {
    color: $cyprus-color;

    &__title {
        padding: 0 1.7rem 1rem;
        border-bottom: 0.1rem solid rgba($black-color, 0.1);
        font-size: 1.2rem;
        line-height: 1.8rem;
        text-transform: uppercase;
    }

    &__row {
        border-bottom: 0.1rem solid rgba($black-color, 0.1);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        @include min-width(lg) {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            padding: 2rem;
            flex-direction: row;
        }
    }

    &__group {
        padding-top: 4.6rem;

        @include min-width(lg) {
            padding-top: 9rem;
        }

        &:last-of-type {
            .transaction-details__row:last-of-type {
                border: none;
            }
        }

        &:first-child {
            padding-top: 4.6rem;
        }
    }

    &__column {
        padding: 2rem;
        min-width: 14rem;
        border-bottom: 0.1rem solid rgba($black-color, 0.1);

        &:last-child {
            border: none;
        }

        @include min-width(lg) {
            padding: 0;
            border: none;
        }
    }

    &__text,
    &__subtitle {
        font-size: 1.5rem;
        line-height: 1.5rem;
    }

    &__subtitle {
        width: 100%;
        font-weight: 700;
    }

    &__text {
        padding-top: 0.7rem;
        overflow: hidden;
        text-overflow: ellipsis;

        &--green {
            color: $lima-color;
            font-weight: 600;

            &:hover {
                cursor: pointer;
                opacity: 0.6;
            }
        }
    }

    &__cancel-order {
        width: 30%;
        flex-wrap: wrap;
        padding: 1rem 0.5rem;
        text-align: center;
    }
}
</style>